import { useMemo } from 'react'
import { useGetIntegrations, useIntegrationTypesWithInstances } from '../../utils/api-client/api-hooks.ts'
import { getIsToolIntegrationType } from '../../utils/repository-utils.tsx'

/**
 * The tool integration types the map form can map onto an existing instance.
 *
 * The counts include the admin-console Polaris instance, which the form refuses because the platform
 * cannot fetch through it, so Polaris counts only once the managed-instance lookup finds one. A
 * pending lookup leaves Polaris out, so it never appears and then vanishes. A failed lookup keeps
 * Polaris: dropping it would hide "Map new" from an org whose only tool is Polaris, and the form
 * still refuses the system instance on its own. The failure stays out of `isError`, which means the
 * counts are unknown and would withhold every unbound row.
 */
export const useMappableIntegrationTypes = () => {
  const { types, isPending, isError } = useIntegrationTypesWithInstances()
  const isPolarisLookupEnabled = types.includes('polaris')
  const managedPolaris = useGetIntegrations({
    type: 'polaris',
    source: 'managed',
    page: { number: 0, size: 1 },
    enabled: isPolarisLookupEnabled,
  })
  const isPolarisMappable = managedPolaris.isError || (managedPolaris.data?.total ?? 0) > 0
  const mappableTypes = useMemo(
    () => types.filter(getIsToolIntegrationType).filter(type => type !== 'polaris' || isPolarisMappable),
    [types, isPolarisMappable]
  )
  return {
    types: mappableTypes,
    // `isPending` rather than `isLoading`, which is false while the browser is offline and a request
    // is paused: the drawer would read the empty counts as "No tools detected", or a list that has
    // silently dropped Polaris.
    isLoading: isPending || (isPolarisLookupEnabled && managedPolaris.isPending),
    isError,
  }
}

/**
 * Whether the map-new form has anything to offer.
 *
 * The form's own precondition, shared so the repository settings drawer's "Map new" gate reads the
 * same answer and cannot offer a form that would render its empty state.
 * `/api/v1/integration-types` omits a tool with no instances, so a reported mappable type is one
 * that has one.
 *
 * A failed counts request answers `true`. The failure is not evidence that nothing is mappable, and
 * closing the gate on it would replace it with "No tools have been connected" — a claim about the
 * org that no response ever made. Open, the form renders its tool control, which reads the same
 * query and says the request failed; the user gets one error in the one place that can explain it.
 */
export const useIsMapToolFormOffered = (): boolean => {
  const { types, isError } = useMappableIntegrationTypes()
  return isError || types.length > 0
}
